import { useState, useRef, useEffect, useCallback, lazy, Suspense, Fragment } from 'react'
import { router } from '@/utils/router'
import { useUserStore } from '@/utils/store'
import { sortGroupsByUnread } from '@/utils/sortGroupsByUnread'
import { useCommunityChat } from '@/hooks/useCommunityChat'
import { useGroupChat } from '@/hooks/useGroupChat'
import { useGroupReadMarker } from '@/hooks/useGroupReadMarker'
import { useIsMobile } from '@/hooks/useIsMobile'
import ErrorBoundary from '@/components/chat/parts/ErrorBoundary'
import { useAieRole } from '@/hooks/useAieRole'
import { apiClient } from '@/utils/api'
import { groupService } from '@/services/group'
import {
  searchUsers,
  fetchGroups,
  fetchGroupDetail,
  getMuteStatus,
  inviteMember,
  fetchInvitedMembers,
  acceptInvite,
  rejectInvite,
  fetchMyInvitations,
  fetchUnread,
  type MentionUser,
  type ChatGroup,
  type GroupInvitation,
  type MuteStatus,
} from '@/services/communityService'

// 合作闯关块懒加载（仅群消息含 aie-coop-game 代码块时加载）
const CoopGameBlock = lazy(() => import('@/components/chat/parts/CoopGameBlock'))

// ─── 内联样式（避免 CSS 加载问题） ──────────────────────

const fabStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  width: 56,
  height: 56,
  borderRadius: 28,
  background: '#FF8C42',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  zIndex: 900,
  transition: 'transform 150ms',
}

/** <768px：全宽 bottom-sheet（替代桌面右下 360×480 小窗，修 375px 溢出） */
const mobilePanelStyle: React.CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '72dvh',
  maxHeight: '72dvh',
  background: '#fff',
  borderRadius: '16px 16px 0 0',
  boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
}

const panelStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 88,
  right: 16,
  width: 360,
  height: 480,
  background: '#fff',
  borderRadius: 16,
  boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const panelTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: '#2C3E50',
}

const closeBtnStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 16,
  border: 'none',
  background: 'transparent',
  color: '#546E7A',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
}

const messagesStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '12px 16px',
  WebkitOverflowScrolling: 'touch',
}

const messageStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  padding: '6px 0',
}

const avatarStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 16,
  flexShrink: 0,
  objectFit: 'cover',
  background: 'linear-gradient(135deg, #FFF3E6, #88D8B0)',
}

const messageBodyStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
}

const messageMetaStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  marginBottom: 2,
}

const messageNameStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#2C3E50',
}

const messageTimeStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#546E7A',
}

const messageContentStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: '#2C3E50',
  wordBreak: 'break-word',
}

const mentionStyle: React.CSSProperties = {
  color: '#FF8C42',
  fontWeight: 600,
}

const inputAreaStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 10,
  padding: '12px 16px',
  borderTop: '1px solid #E0E6ED',
  background: '#fff',
  flexShrink: 0,
  position: 'relative',
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  resize: 'none',
  border: '1px solid #E0E6ED',
  borderRadius: 8,
  padding: 10,
  fontSize: 14,
  lineHeight: 1.5,
  maxHeight: 72,
  outline: 'none',
  color: '#2C3E50',
  background: '#FAF8F5',
  fontFamily: 'inherit',
}

const sendBtnStyle = (disabled: boolean): React.CSSProperties => ({
  width: 36,
  height: 36,
  borderRadius: 18,
  border: 'none',
  background: disabled ? '#ccc' : '#FF8C42',
  color: '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  opacity: disabled ? 0.5 : 1,
})

const mentionDropdownStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '100%',
  left: 16,
  right: 16,
  background: '#fff',
  border: '1px solid #E0E6ED',
  borderRadius: 8,
  boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
  maxHeight: 200,
  overflowY: 'auto',
  zIndex: 1001,
}

const mentionItemStyle = (active: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
  cursor: 'pointer',
  background: active ? '#FFF3E6' : 'transparent',
})

const mentionItemAvatarStyle: React.CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: 12,
  background: 'linear-gradient(135deg, #FFF3E6, #88D8B0)',
  objectFit: 'cover',
}

const toastStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 88,
  right: 16,
  maxWidth: 320,
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  zIndex: 950,
  cursor: 'pointer',
}

const emptyStyle: React.CSSProperties = {
  textAlign: 'center',
  color: '#546E7A',
  padding: '40px 16px',
  fontSize: 14,
}

// ─── Tab 样式 ─────────────────────────────────────────────

const tabsContainerStyle: React.CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid #E0E6ED',
  flexShrink: 0,
  background: '#fff',
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  flex: 1,
  padding: '12px 8px',
  border: 'none',
  background: 'transparent',
  fontSize: 14,
  fontWeight: 600,
  color: active ? '#FF8C42' : '#546E7A',
  cursor: 'pointer',
  borderBottom: active ? '2px solid #FF8C42' : '2px solid transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  transition: 'all 150ms',
})

// ─── 群组列表样式 ─────────────────────────────────────────

const groupsContainerStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '12px 16px',
  WebkitOverflowScrolling: 'touch',
}

const groupCardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E0E6ED',
  borderRadius: 12,
  padding: '12px',
  marginBottom: 10,
  cursor: 'pointer',
  transition: 'all 150ms',
}

const groupCardHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 8,
}

const groupAvatarStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 12,
  background: 'linear-gradient(135deg, #FFF3E6, #88D8B0)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
  flexShrink: 0,
}

const groupInfoStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
}

const groupNameStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: '#2C3E50',
  marginBottom: 2,
}

const groupDescStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#546E7A',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const groupMetaStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  fontSize: 12,
  color: '#546E7A',
}

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #E0E6ED',
  borderRadius: 8,
  fontSize: 14,
  outline: 'none',
  background: '#FAF8F5',
  marginBottom: 12,
}

const createGroupBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px',
  border: 'none',
  borderRadius: 8,
  background: '#FF8C42',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  marginBottom: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
}

const applyBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  border: 'none',
  borderRadius: 6,
  background: '#88D8B0',
  color: '#fff',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  marginTop: 8,
  width: '100%',
}

const redDotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 4,
  background: '#FF5252',
  marginLeft: 4,
}

// ─── 群聊视图样式 ─────────────────────────────────────────

const groupChatHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 16px',
  borderBottom: '1px solid #E0E6ED',
  flexShrink: 0,
  background: '#fff',
}

const backBtnStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 16,
  border: 'none',
  background: 'transparent',
  color: '#546E7A',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
}

const approveBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  border: 'none',
  borderRadius: 6,
  background: '#FF8C42',
  color: '#fff',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}

// ─── 审批列表样式 ─────────────────────────────────────────

const applicantsModalStyle: React.CSSProperties = {
  position: 'absolute',
  top: 48,
  right: 16,
  width: 280,
  background: '#fff',
  border: '1px solid #E0E6ED',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  zIndex: 1002,
  maxHeight: 320,
  overflowY: 'auto',
}

const applicantItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  borderBottom: '1px solid #E0E6ED',
}

// ─── 禁言提示样式 ─────────────────────────────────────────

const mutedBannerStyle: React.CSSProperties = {
  padding: '12px 16px',
  background: '#FFF3E6',
  borderBottom: '1px solid #FFD180',
  fontSize: 13,
  color: '#FF8C42',
  textAlign: 'center',
  flexShrink: 0,
}

const mutedInputStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderTop: '1px solid #E0E6ED',
  background: '#FAFAFA',
  fontSize: 13,
  color: '#546E7A',
  textAlign: 'center',
}

// ─── Mention 解析 ─────────────────────────────────────────

function extractMentionQuery(
  text: string,
  cursorPos: number,
): { query: string; startIndex: number } | null {
  const beforeCursor = text.slice(0, cursorPos)
  const atIndex = beforeCursor.lastIndexOf('@')
  if (atIndex === -1) return null
  const afterAt = beforeCursor.slice(atIndex + 1)
  if (/[\s@]/.test(afterAt)) return null
  return { query: afterAt, startIndex: atIndex }
}

/** 渲染群消息内容：@mention 高亮 + aie-coop-game 代码块懒加载 CoopGameBlock（合作闯关内嵌）。
 *  其余纯文本保持原样。agent 回复的合作闯关消息含 ```aie-coop-game {sessionId}``` 块。 */
function renderContent(content: string): React.ReactNode[] {
  // 1) 先切出 ```aie-coop-game ... ``` 代码块
  const coopBlockRe = /```aie-coop-game\s*([\s\S]*?)```/g
  const segments: Array<{ type: 'text'; value: string } | { type: 'coop'; payload: string }> = []
  let lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = coopBlockRe.exec(content)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, m.index) })
    }
    segments.push({ type: 'coop', payload: m[1] })
    lastIndex = coopBlockRe.lastIndex
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) })
  }
  // 无代码块 → 走原 @mention 逻辑
  if (segments.length === 1 && segments[0].type === 'text') {
    return renderMentionText(segments[0].value)
  }
  // 混合：文本段走 @mention，coop 段渲染 CoopGameBlock
  return segments.map((seg, i) => {
    if (seg.type === 'coop') {
      const sm = seg.payload.match(/"sessionId"\s*:\s*"([^"]+)"/) || seg.payload.match(/^\s*([a-f0-9]{24})\s*$/i)
      const sid = sm?.[1]
      if (!sid) return null
      return (
        <Suspense key={i} fallback={<div style={{ padding: 8, color: '#999', fontSize: 13 }}>🤝 加载合作闯关…</div>}>
          <ErrorBoundary><CoopGameBlock sessionId={sid} /></ErrorBoundary>
        </Suspense>
      )
    }
    return <Fragment key={i}>{renderMentionText(seg.value)}</Fragment>
  })
}

/** 纯文本 + @mention 高亮（原 renderContent 的 @mention 部分） */
function renderMentionText(text: string): React.ReactNode[] {
  const parts = text.split(/(@\S+)/g)
  return parts.map((part, i) =>
    part.startsWith('@') ? (
      <span key={i} style={mentionStyle}>{part}</span>
    ) : (
      part
    ),
  )
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  const time = d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  if (isToday) return time
  return `${d.getMonth() + 1}/${d.getDate()} ${time}`
}

function useDebounce<T extends (...args: never[]) => void>(
  fn: T,
  delay: number,
): T {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  return useCallback(
    ((...args: Parameters<T>) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => fn(...args), delay)
    }) as T,
    [fn, delay],
  )
}

// ─── 主组件 ───────────────────────────────────────────────

export default function CommunityChat() {
  const isAuthenticated = useUserStore((s) => s.isAuthenticated)
  const currentUser = useUserStore((s) => s.user)
  // 手机端 FAB 上移避开聊天输入区/发送按钮（实测 bottom:100 边缘仍接触，132 彻底分开；
  // 上移后仅消息滚到底时轻触最后一条消息右上角，不影响正文阅读）
  const isMobile = useIsMobile()

  // 面板状态（【隐私收紧 2026-10-03】「群组」公开浏览 tab 已移除，只留公共/我的；
  // 加入途径：邀请码（下方输入卡）或群主主动邀请）
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'public' | 'mine'>('public')

  // 管理员可删除公共/群组中任何人的发言（内容合规）
  const { isAdmin } = useAieRole()

  // 群组相关
  const [groups, setGroups] = useState<ChatGroup[]>([])
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null)
  // 收到的群邀请（红点 + 「我的」tab 顶部邀请卡片区）
  const [invitations, setInvitations] = useState<GroupInvitation[]>([])
  // 群消息未读（FAB 徽标 / tab 徽标 / 群卡片气泡 / 新消息 toast）
  const [unread, setUnread] = useState<{ total: number; byGroup: Map<string, number> }>({ total: 0, byGroup: new Map() })
  const [unreadToast, setUnreadToast] = useState<string | null>(null)
  const lastNotifiedTotalRef = useRef<number | null>(null)
  const unreadToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 公共聊天
  const publicChat = useCommunityChat(isOpen && activeTab === 'public' && !activeGroupId)

  // 凭邀请码加入（「我的」tab 内联卡片：输码 → 验证显群名 → 确认进群）
  const [showJoinByCode, setShowJoinByCode] = useState(false)
  const [joinCodeInput, setJoinCodeInput] = useState('')
  const [joinPreview, setJoinPreview] = useState<{ groupId: string; groupName: string; groupType: string } | null>(null)
  const [validatingCode, setValidatingCode] = useState(false)
  const [joiningByCode, setJoiningByCode] = useState(false)
  const [joinCodeError, setJoinCodeError] = useState<string | null>(null)

  // 邀请相关
  const [showInvite, setShowInvite] = useState(false)
  const [inviteSearch, setInviteSearch] = useState('')
  const [inviteResults, setInviteResults] = useState<MentionUser[]>([])
  const [invitingUserId, setInvitingUserId] = useState<string | null>(null)
  const [invitedMembers, setInvitedMembers] = useState<Array<{userId: string; name: string; avatar: string}>>([])
  const [showInvitedList, setShowInvitedList] = useState(false)
  const [handlingInviteGroupId, setHandlingInviteGroupId] = useState<string | null>(null)
  // 邀请成败反馈（成功=等待回复/已秒接受，失败=后端中文文案）
  const [inviteFeedback, setInviteFeedback] = useState<{ text: string; kind: 'success' | 'error' } | null>(null)

  // 当前群成员（@mention 只列群成员——群消息仅成员可见，@ 群外人无意义）
  const [groupMembers, setGroupMembers] = useState<MentionUser[]>([])

  // 群聊
  const groupChat = useGroupChat(activeGroupId, isOpen && activeGroupId !== null)

  // 禁言状态
  const [muteStatus, setMuteStatus] = useState<MuteStatus>({ muted: false })

  // 输入相关
  const [inputValue, setInputValue] = useState('')
  const [sendError, setSendError] = useState<string | null>(null)
  const [mentionDropdown, setMentionDropdown] = useState<{
    query: string
    startIndex: number
  } | null>(null)
  const [mentionResults, setMentionResults] = useState<MentionUser[]>([])
  const [activeMentionIndex, setActiveMentionIndex] = useState(0)
  const [pendingMentions, setPendingMentions] = useState<Map<string, string>>(new Map())

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesListRef = useRef<HTMLDivElement>(null)

  // ─── 加载群组数据 ───────────────────────────────────────

  useEffect(() => {
    if (isOpen && activeTab === 'mine' && !activeGroupId) {
      fetchGroups('joined')
        .then(res => setGroups(res.data))
        .catch(() => setGroups([]))
      fetchMyInvitations()
        .then(setInvitations)
        .catch(() => setInvitations([]))
    }
  }, [isOpen, activeTab, activeGroupId])

  // 面板关闭时的低频感知轮询（30s）：邀请数 + 群消息未读。
  // 打开浮窗即停（群聊视图自有 5s 实时轮询；邀请在 tab effect 里刷新）
  useEffect(() => {
    if (isOpen || !isAuthenticated) return

    const refresh = () => {
      fetchMyInvitations()
        .then(setInvitations)
        .catch(() => {})
      fetchUnread()
        .then((data) => {
          const byGroup = new Map<string, number>()
          for (const g of data.groups || []) byGroup.set(String(g.groupId), g.count)
          setUnread({ total: data.total || 0, byGroup })
          // 首拉只记基线不弹 toast；之后总数增长才提示
          if (lastNotifiedTotalRef.current === null) {
            lastNotifiedTotalRef.current = data.total || 0
          } else if ((data.total || 0) > lastNotifiedTotalRef.current) {
            lastNotifiedTotalRef.current = data.total || 0
            setUnreadToast(`💬 你的群有 ${data.total} 条新发言`)
            if (unreadToastTimerRef.current) clearTimeout(unreadToastTimerRef.current)
            unreadToastTimerRef.current = setTimeout(() => setUnreadToast(null), 3000)
          } else {
            lastNotifiedTotalRef.current = data.total || 0
          }
        })
        .catch(() => {})
    }
    refresh()
    const timer = setInterval(refresh, 30_000)
    return () => clearInterval(timer)
  }, [isOpen, isAuthenticated])

  // ─── 加载禁言状态 ───────────────────────────────────────

  useEffect(() => {
    if (isOpen && activeTab === 'public' && !activeGroupId) {
      getMuteStatus()
        .then(setMuteStatus)
        .catch(() => setMuteStatus({ muted: false }))
    }
  }, [isOpen, activeTab, activeGroupId])

  // 进群/快速退出/群内新消息 三路径已读标记（useGroupReadMarker）。
  // 原 800ms 延时标记的 clearTimeout 会吞掉快速浏览的标记——用户点进群看一眼
  // 就返回，看了却没标记，红点永不消；现退出兜底立即标记 + 新消息滚动标记。
  const clearUnreadForGroup = useCallback((gid: string) => {
    setUnread((prev) => {
      const count = prev.byGroup.get(gid) || 0
      if (count === 0) return prev
      const byGroup = new Map(prev.byGroup)
      byGroup.delete(gid)
      return { total: Math.max(0, prev.total - count), byGroup }
    })
  }, [])
  useGroupReadMarker(activeGroupId, groupChat.messages, clearUnreadForGroup)

  // ─── 加载已邀请列表 ───────────────────────────────────────

  useEffect(() => {
    if (activeGroupId && showInvitedList) {
      fetchInvitedMembers(activeGroupId)
        .then(setInvitedMembers)
        .catch(() => setInvitedMembers([]))
    }
  }, [activeGroupId, showInvitedList])

  // ─── 加载当前群成员（供 @mention 本地过滤） ───────────────────

  useEffect(() => {
    // 切群时清空 @ 提及状态——旧群的成员列表/已选提及不跨群残留
    // （2026-10-15 案例：A 群 @ 出列表后切 B 群，下拉仍显示 A 群成员）
    setMentionDropdown(null)
    setMentionResults([])
    setActiveMentionIndex(0)
    setPendingMentions(new Map())
    if (!activeGroupId) {
      setGroupMembers([])
      return
    }
    fetchGroupDetail(activeGroupId)
      .then((detail) => {
        setGroupMembers(detail.members.map((m) => ({
          _id: m.userId, name: m.name, username: '', avatar: m.avatar,
        })))
      })
      .catch(() => setGroupMembers([]))
  }, [activeGroupId])

  // ─── 邀请搜索 ───────────────────────────────────────────────

  useEffect(() => {
    if (!showInvite) {
      setInviteResults([])
      return
    }
    // 打开面板或输入变化时搜索（空关键词返回前 10 个用户）
    const query = inviteSearch.trim()
    const timer = setTimeout(() => {
      searchUsers(query)
        .then(setInviteResults)
        .catch(() => setInviteResults([]))
    }, query ? 300 : 0)
    return () => clearTimeout(timer)
  }, [showInvite, inviteSearch])

  // ─── 自动滚动 ───────────────────────────────────────────

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [activeGroupId ? groupChat.messages.length : publicChat.messages.length, isOpen, activeGroupId])

  // ─── @mention 搜索 ─────────────────────────────────────────

  const doSearch = useCallback(async (query: string) => {
    // 群聊 @ 只列群成员（群消息仅成员可见）；公共聊天保持全平台搜索
    if (activeGroupId) {
      const q = query.trim().toLowerCase()
      setMentionResults(q
        ? groupMembers.filter((u) => (u.name || u.username || '').toLowerCase().includes(q))
        : groupMembers)
      setActiveMentionIndex(0)
      return
    }
    try {
      const users = await searchUsers(query || ' ')
      setMentionResults(users)
      setActiveMentionIndex(0)
    } catch { setMentionResults([]) }
  }, [activeGroupId, groupMembers])

  const debouncedSearch = useDebounce(doSearch, 300)

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const text = e.target.value
      setInputValue(text)
      const el = e.target
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 72) + 'px'

      const cursorPos = el.selectionStart ?? text.length
      const mention = extractMentionQuery(text, cursorPos)
      if (mention) {
        setMentionDropdown(mention)
        debouncedSearch(mention.query)
      } else {
        setMentionDropdown(null)
        setMentionResults([])
      }
    },
    [debouncedSearch],
  )

  const selectMention = useCallback(
    (user: MentionUser) => {
      if (!mentionDropdown) return
      const displayName = user.name || user.username || user._id
      const before = inputValue.slice(0, mentionDropdown.startIndex)
      const afterAt = inputValue.slice(
        mentionDropdown.startIndex + 1 + mentionDropdown.query.length,
      )
      const newText = `${before}@${displayName} ${afterAt}`
      setInputValue(newText)
      setMentionDropdown(null)
      setMentionResults([])
      setPendingMentions((prev) => {
        const next = new Map(prev)
        next.set(displayName, user._id)
        return next
      })
      inputRef.current?.focus()
    },
    [mentionDropdown, inputValue],
  )

  const currentChat = activeGroupId ? groupChat : publicChat

  const handleSend = useCallback(async () => {
    const text = inputValue.trim()
    if (!text || currentChat.sending) return
    const mentionIds: string[] = []
    for (const [name, id] of pendingMentions) {
      if (text.includes(`@${name}`)) mentionIds.push(id)
    }
    try {
      if (activeGroupId) {
        await groupChat.sendMessage(text, mentionIds)
      } else {
        await publicChat.sendMessage(text, mentionIds)
      }
      setInputValue('')
      setPendingMentions(new Map())
      if (inputRef.current) inputRef.current.style.height = 'auto'
    } catch (err) {
      // 后端中文文案（禁言/无权限等）已由 apiClient 透传到 err.message
      setSendError(err instanceof Error && err.message ? err.message : '发送失败，请稍后重试')
      setTimeout(() => setSendError(null), 4000)
    }
  }, [inputValue, currentChat.sending, pendingMentions, publicChat, groupChat, activeGroupId])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (mentionDropdown && mentionResults.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setActiveMentionIndex((i) => (i < mentionResults.length - 1 ? i + 1 : 0))
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setActiveMentionIndex((i) => (i > 0 ? i - 1 : mentionResults.length - 1))
          return
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault()
          selectMention(mentionResults[activeMentionIndex])
          return
        }
        if (e.key === 'Escape') {
          setMentionDropdown(null)
          setMentionResults([])
          return
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [mentionDropdown, mentionResults, activeMentionIndex, selectMention, handleSend],
  )

  const handleDelete = useCallback(
    async (messageId: string) => {
      try {
        if (activeGroupId) {
          await groupChat.deleteMessage(messageId)
        } else {
          await publicChat.deleteMessage(messageId)
        }
      } catch { /* 静默 */ }
    },
    [activeGroupId, groupChat, publicChat],
  )

  const handleScroll = useCallback(() => {
    const el = messagesListRef.current
    if (!el) return
    if (el.scrollTop < 50 && !publicChat.loadingHistory && !activeGroupId) {
      publicChat.loadHistory()
    }
  }, [publicChat.loadingHistory, publicChat.loadHistory, activeGroupId])

  // ─── 群组操作 ───────────────────────────────────────────

  /** 验证邀请码：显示群名预览（错误文案透传后端中文：已过期/已使用/无效等）。
   *  直调原始端点而非 groupService.validateCode——后者 catch 后返回 null 会吞掉文案。 */
  const handleValidateCode = useCallback(async () => {
    const code = joinCodeInput.trim().toUpperCase()
    if (!code) return
    setValidatingCode(true)
    setJoinCodeError(null)
    try {
      const res = await apiClient.post<{ data: { groupId: string; groupName: string; groupType: string } }>(
        `/aie/group/validate-invite/${encodeURIComponent(code)}`,
      )
      setJoinPreview(res.data)
    } catch (err) {
      setJoinCodeError(err instanceof Error && err.message ? err.message : '邀请码无效')
    } finally {
      setValidatingCode(false)
    }
  }, [joinCodeInput])

  /** 凭码加入（直接进群），成功后刷新「我的」列表并收起卡片 */
  const handleJoinByCode = useCallback(async () => {
    const code = joinCodeInput.trim().toUpperCase()
    if (!code || joiningByCode) return
    setJoiningByCode(true)
    setJoinCodeError(null)
    try {
      await groupService.joinByCode(code)
      setShowJoinByCode(false)
      setJoinCodeInput('')
      setJoinPreview(null)
      const res = await fetchGroups('joined')
      setGroups(res.data)
    } catch (err) {
      setJoinCodeError(err instanceof Error && err.message ? err.message : '加入失败，请稍后重试')
    } finally {
      setJoiningByCode(false)
    }
  }, [joinCodeInput, joiningByCode])

  const handleInviteMember = useCallback(async (targetUserId: string, targetName: string) => {
    if (!activeGroupId) return
    setInvitingUserId(targetUserId)
    try {
      await inviteMember(activeGroupId, targetUserId)
      // 刷新已邀请列表
      const list = await fetchInvitedMembers(activeGroupId)
      setInvitedMembers(list)
      setInviteSearch('')
      setInviteResults([])
      // 刷新后不在等待列表 = 对方已接受邀请直接进群（「已邀请」只显示等待回复的人）
      const stillPending = list.some((m) => m.userId === targetUserId)
      setInviteFeedback(stillPending
        ? { text: `已向 ${targetName} 发出邀请，等待回复`, kind: 'success' }
        : { text: `${targetName} 已接受邀请，已加入群`, kind: 'success' })
    } catch (err) {
      // 后端中文文案（该用户已是群成员/已向该用户发送过邀请等）已由 apiClient 透传到 err.message
      setInviteFeedback({
        text: err instanceof Error && err.message ? err.message : '邀请失败，请稍后重试',
        kind: 'error',
      })
    } finally {
      setInvitingUserId(null)
    }
  }, [activeGroupId])

  const handleAcceptInvite = useCallback(async (groupId: string) => {
    setHandlingInviteGroupId(groupId)
    try {
      await acceptInvite(groupId)
      // 更新本地状态：invited → approved
      setGroups(prev => prev.map(g =>
        g._id === groupId ? { ...g, myRole: 'approved' } : g
      ))
      setInvitations(prev => prev.filter(i => i.groupId !== groupId))
      // 「我的」列表立即出现新群（否则要等切 tab 触发刷新）
      if (activeTab === 'mine') {
        fetchGroups('joined')
          .then(res => setGroups(res.data))
          .catch(() => {})
      }
    } catch { /* 静默 */ } finally {
      setHandlingInviteGroupId(null)
    }
  }, [activeTab])

  const handleRejectInvite = useCallback(async (groupId: string) => {
    setHandlingInviteGroupId(groupId)
    try {
      await rejectInvite(groupId)
      // 从列表移除
      setGroups(prev => prev.map(g =>
        g._id === groupId ? { ...g, myRole: null } : g
      ))
      setInvitations(prev => prev.filter(i => i.groupId !== groupId))
    } catch { /* 静默 */ } finally {
      setHandlingInviteGroupId(null)
    }
  }, [])

  // ─── 当前消息列表 ───────────────────────────────────────

  const currentMessages = activeGroupId ? groupChat.messages : publicChat.messages
  const currentLoadingHistory = activeGroupId ? false : publicChat.loadingHistory
  const currentToast = activeGroupId ? null : publicChat.toastMessage

  // ─── 当前群组信息 ───────────────────────────────────────

  const currentGroup = activeGroupId ? groups.find(g => g._id === activeGroupId) : null
  const isGroupOwner = currentGroup?.myRole === 'owner'

  if (!isAuthenticated) return null

  return (
    <>
      {/* FAB */}
      {!isOpen && (
        <button
          style={isMobile ? { ...fabStyle, bottom: 132 } : fabStyle}
          onClick={() => setIsOpen(true)}
          aria-label="打开社区聊天"
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)' }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)' }}
        >
          <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" />
            <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z" />
          </svg>
          {(() => {
            const badge = invitations.length + unread.total
            if (badge === 0) return null
            return (
              <span
                aria-label={`${badge} 条待处理（群邀请 ${invitations.length} + 群消息未读 ${unread.total}）`}
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  minWidth: 18,
                  height: 18,
                  padding: '0 4px',
                  borderRadius: 9,
                  background: '#FF5252',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: '18px',
                  textAlign: 'center',
                  border: '2px solid #fff',
                  boxSizing: 'content-box',
                }}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            )
          })()}
        </button>
      )}

      {/* 群未读 Toast（浮窗关闭时新发言提醒，点击直达第一个未读群） */}
      {unreadToast && !isOpen && !currentToast && (
        <div
          style={isMobile ? { ...toastStyle, bottom: 196 } : toastStyle}
          role="button"
          tabIndex={0}
          onClick={() => {
            setIsOpen(true)
            // 直达第一个有未读的群（进去即自动标记已读）；无未读群时回退「我的」
            const firstUnread = [...unread.byGroup.keys()][0]
            if (firstUnread) setActiveGroupId(firstUnread)
            else setActiveTab('mine')
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.currentTarget.click()
            }
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: '#2C3E50', whiteSpace: 'nowrap' }}>
            {unreadToast}
          </div>
        </div>
      )}

      {/* Toast */}
      {currentToast && !isOpen && (
        <div
          style={isMobile ? { ...toastStyle, bottom: 196 } : toastStyle}
          role="button"
          tabIndex={0}
          onClick={() => setIsOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.currentTarget.click();
            }
          }}
        >

          <img
            style={avatarStyle}
            src={currentToast.avatar || undefined}
            alt=""
          />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#2C3E50' }}>
              {currentToast.username}
            </div>
            <div style={{ fontSize: 12, color: '#546E7A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentToast.content}
            </div>
          </div>
        </div>
      )}

      {/* 点击外部关闭 */}
      {isOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          onClick={() => {
            setIsOpen(false)
            setActiveTab('public')
            setActiveGroupId(null)
          }}
        />
      )}

      {/* 面板 */}
      {isOpen && (
        <div style={isMobile ? mobilePanelStyle : panelStyle}>
          {/* 如果在群聊中，显示返回按钮 */}
          {activeGroupId ? (
            <div style={groupChatHeaderStyle}>
              <button
                style={backBtnStyle}
                onClick={() => setActiveGroupId(null)}
                aria-label="返回"
              >
                ←
              </button>
              <span style={{ ...panelTitleStyle, flex: 1 }}>
                {currentGroup?.name || '群聊'}
              </span>
              {isGroupOwner && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    style={approveBtnStyle}
                    onClick={() => {
                      setShowInvite(!showInvite)
                      setShowInvitedList(false)
                      setInviteSearch('')
                      setInviteResults([])
                      setInviteFeedback(null)
                    }}
                  >
                    邀请
                  </button>
                  <button
                    style={{ ...approveBtnStyle, position: 'relative' }}
                    onClick={() => {
                      setShowInvitedList(!showInvitedList)
                      setShowInvite(false)
                    }}
                  >
                    已邀请
                    {invitedMembers.length > 0 && (
                      <span style={{
                        ...redDotStyle,
                        background: '#FF8C42',
                      }} />
                    )}
                  </button>
                </div>
              )}

              {/* 邀请搜索面板（候选≠群成员：列出的是可邀请的全站用户） */}
              {isGroupOwner && showInvite && (
                <div style={{ ...applicantsModalStyle, left: 80, width: 240 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#2C3E50', padding: '2px 2px 6px' }}>
                    邀请用户加入群
                  </div>
                  <div style={{ fontSize: 11, color: '#90A4AE', padding: '0 2px 8px' }}>
                    列出的是平台用户，不是本群成员
                  </div>
                  {inviteFeedback && (
                    <div style={{
                      fontSize: 12,
                      color: inviteFeedback.kind === 'success' ? '#4CAF50' : '#E53935',
                      padding: '4px 2px 8px',
                      lineHeight: 1.4,
                    }}>
                      {inviteFeedback.text}
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="搜索用户..."
                    value={inviteSearch}
                    onChange={(e) => setInviteSearch(e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '8px 10px',
                      border: '1px solid #E0E0E0',
                      borderRadius: 8,
                      fontSize: 13,
                      outline: 'none',
                      marginBottom: 8,
                    }}
                  />
                  {inviteResults.length > 0 ? (
                    <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                      {inviteResults
                        // 排除自己、已在群的成员、已发邀请等待回复的人——剩下的才是可邀请对象
                        .filter(user =>
                          user._id !== currentUser?.id &&
                          !groupMembers.some(m => m._id === user._id) &&
                          !invitedMembers.some(m => m.userId === user._id))
                        .map(user => (
                        <div
                          key={user._id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 4px',
                            borderRadius: 6,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleInviteMember(user._id, user.name || user.username)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F0F0F0'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent'
                          }}
                        >
                          <img
                            style={{ ...avatarStyle, width: 24, height: 24 }}
                            src={user.avatar || undefined}
                            alt=""
                          />
                          <span style={{ fontSize: 13, color: '#2C3E50', flex: 1 }}>
                            {user.name || user.username}
                          </span>
                          {invitingUserId === user._id ? (
                            <span style={{ fontSize: 12, color: '#999' }}>...</span>
                          ) : (
                            <span style={{ fontSize: 12, color: '#FF8C42' }}>邀请</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#999', textAlign: 'center', padding: 8 }}>
                      {inviteSearch.trim() ? '未找到用户' : '加载中...'}
                    </div>
                  )}
                  {inviteResults.length > 0 &&
                    inviteResults.every(user =>
                      user._id === currentUser?.id ||
                      groupMembers.some(m => m._id === user._id) ||
                      invitedMembers.some(m => m.userId === user._id)) && (
                    <div style={{ fontSize: 12, color: '#999', textAlign: 'center', padding: 8 }}>
                      都已在本群或已邀请
                    </div>
                  )}
                </div>
              )}

              {/* 已邀请列表 */}
              {isGroupOwner && showInvitedList && (
                <div style={{ ...applicantsModalStyle, right: 10, width: 220 }}>
                  {invitedMembers.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#999', textAlign: 'center', padding: 12 }}>
                      还没有发出邀请
                    </div>
                  ) : (
                    invitedMembers.map(m => (
                      <div key={m.userId} style={applicantItemStyle}>
                        <img
                          style={{ ...avatarStyle, width: 28, height: 28 }}
                          src={m.avatar || undefined}
                          alt=""
                        />
                        <span style={{ fontSize: 13, color: '#2C3E50', flex: 1 }}>
                          {m.name}
                        </span>
                        <span style={{ fontSize: 11, color: '#FF8C42' }}>等待回复</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Tab 栏 */
            <div style={tabsContainerStyle}>
              <button
                style={tabStyle(activeTab === 'public')}
                onClick={() => setActiveTab('public')}
              >
                💬 公共
              </button>
              <button
                style={tabStyle(activeTab === 'mine')}
                onClick={() => setActiveTab('mine')}
              >
                📌 我的
                {(invitations.length > 0 || unread.total > 0) && (
                  <span
                    style={{
                      display: 'inline-block',
                      marginLeft: 4,
                      padding: '0 6px',
                      borderRadius: 8,
                      background: '#FF5252',
                      color: '#fff',
                      fontSize: 11,
                      lineHeight: '16px',
                      verticalAlign: 'middle',
                    }}
                  >
                    {invitations.length + unread.total}
                  </span>
                )}
              </button>
              <button
                style={closeBtnStyle}
                onClick={() => {
                  setIsOpen(false)
                  setActiveTab('public')
                  setActiveGroupId(null)
                }}
              >
                ✕
              </button>
            </div>
          )}

          {/* 内容区域 */}
          {activeGroupId ? (
            /* 群聊消息 */
            <>
              {muteStatus.muted ? (
                <div style={mutedBannerStyle}>
                  {muteStatus.until
                    ? `您已被禁言，至 ${new Date(muteStatus.until).toLocaleDateString('zh-CN')}`
                    : '您已被禁言'}
                </div>
              ) : null}
              <div
                style={messagesStyle}
                ref={messagesListRef}
                onScroll={handleScroll}
              >
                {currentLoadingHistory && (
                  <div style={{ textAlign: 'center', padding: 8, fontSize: 12, color: '#546E7A' }}>
                    加载中...
                  </div>
                )}
                {currentMessages.length === 0 && (
                  <div style={emptyStyle}>还没有消息，来打个招呼吧 👋</div>
                )}
                {currentMessages.map((msg) => {
                  const isMine = msg.userId === currentUser?.id
                  const canDelete = isMine || isAdmin
                  return (
                    <div key={msg._id} style={{ ...messageStyle, position: 'relative' }}>
                      {msg.avatar ? (
                        <img style={avatarStyle} src={msg.avatar} alt={msg.username} />
                      ) : (
                        <div style={avatarStyle} />
                      )}
                      <div style={messageBodyStyle}>
                        <div style={messageMetaStyle}>
                          <span style={messageNameStyle}>{msg.username}</span>
                          {msg.senderType === 'agent' && (
                            <span style={{ fontSize: 10, color: '#fff', background: '#0F204B', borderRadius: 4, padding: '1px 5px' }}>辅导员</span>
                          )}
                          <span style={messageTimeStyle}>{formatTime(msg.createdAt)}</span>
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(msg._id)}
                              style={{ background: 'none', border: 'none', color: '#546E7A', cursor: 'pointer', fontSize: 12, padding: '0 4px', lineHeight: 1 }}
                              title="删除"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <div style={messageContentStyle}>{renderContent(msg.content)}</div>
                      </div>
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>
              {sendError && (
                <div
                  role="alert"
                  style={{
                    ...mutedBannerStyle,
                    borderTop: '1px solid #FFCDD2',
                    background: '#FFEBEE',
                    color: '#C62828',
                  }}
                >
                  {sendError}
                </div>
              )}
              <div style={inputAreaStyle}>
                {mentionDropdown && mentionResults.length > 0 && (
                  <div style={mentionDropdownStyle}>
                    {mentionResults.map((user, i) => (
                      <div
                        key={user._id}
                        style={mentionItemStyle(i === activeMentionIndex)}
                        onClick={() => selectMention(user)}
                        onMouseEnter={() => setActiveMentionIndex(i)}
                      >
                        <img
                          style={mentionItemAvatarStyle}
                          src={user.avatar || undefined}
                          alt=""
                        />
                        <span style={{ fontSize: 14, color: '#2C3E50' }}>
                          {user.name || user.username}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {muteStatus.muted ? (
                  <div style={mutedInputStyle}>
                    {muteStatus.until
                      ? `您已被禁言，至 ${new Date(muteStatus.until).toLocaleDateString('zh-CN')}`
                      : '您已被禁言'}
                  </div>
                ) : (
                  <>
                    <textarea
                      ref={inputRef}
                      style={inputStyle}
                      value={inputValue}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      placeholder="说点什么... 输入 @ 可以 @其他人"
                      rows={1}
                    />
                    <button
                      style={sendBtnStyle(!inputValue.trim() || currentChat.sending)}
                      onClick={handleSend}
                      disabled={!inputValue.trim() || currentChat.sending}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </>
          ) : activeTab === 'public' ? (
            /* 公共聊天 */
            <>
              {muteStatus.muted ? (
                <div style={mutedBannerStyle}>
                  {muteStatus.until
                    ? `您已被禁言，至 ${new Date(muteStatus.until).toLocaleDateString('zh-CN')}`
                    : '您已被禁言'}
                </div>
              ) : null}
              <div
                style={messagesStyle}
                ref={messagesListRef}
                onScroll={handleScroll}
              >
                {publicChat.loadingHistory && (
                  <div style={{ textAlign: 'center', padding: 8, fontSize: 12, color: '#546E7A' }}>
                    加载中...
                  </div>
                )}
                {publicChat.messages.length === 0 && (
                  <div style={emptyStyle}>还没有消息，来打个招呼吧 👋</div>
                )}
                {publicChat.messages.map((msg) => {
                  const isMine = msg.userId === currentUser?.id
                  const canDelete = isMine || isAdmin
                  return (
                    <div key={msg._id} style={{ ...messageStyle, position: 'relative' }}>
                      {msg.avatar ? (
                        <img style={avatarStyle} src={msg.avatar} alt={msg.username} />
                      ) : (
                        <div style={avatarStyle} />
                      )}
                      <div style={messageBodyStyle}>
                        <div style={messageMetaStyle}>
                          <span style={messageNameStyle}>{msg.username}</span>
                          {msg.senderType === 'agent' && (
                            <span style={{ fontSize: 10, color: '#fff', background: '#0F204B', borderRadius: 4, padding: '1px 5px' }}>辅导员</span>
                          )}
                          <span style={messageTimeStyle}>{formatTime(msg.createdAt)}</span>
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(msg._id)}
                              style={{ background: 'none', border: 'none', color: '#546E7A', cursor: 'pointer', fontSize: 12, padding: '0 4px', lineHeight: 1 }}
                              title="删除"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <div style={messageContentStyle}>{renderContent(msg.content)}</div>
                      </div>
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>
              {sendError && (
                <div
                  role="alert"
                  style={{
                    ...mutedBannerStyle,
                    borderTop: '1px solid #FFCDD2',
                    background: '#FFEBEE',
                    color: '#C62828',
                  }}
                >
                  {sendError}
                </div>
              )}
              <div style={inputAreaStyle}>
                {mentionDropdown && mentionResults.length > 0 && (
                  <div style={mentionDropdownStyle}>
                    {mentionResults.map((user, i) => (
                      <div
                        key={user._id}
                        style={mentionItemStyle(i === activeMentionIndex)}
                        onClick={() => selectMention(user)}
                        onMouseEnter={() => setActiveMentionIndex(i)}
                      >
                        <img
                          style={mentionItemAvatarStyle}
                          src={user.avatar || undefined}
                          alt=""
                        />
                        <span style={{ fontSize: 14, color: '#2C3E50' }}>
                          {user.name || user.username}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {muteStatus.muted ? (
                  <div style={mutedInputStyle}>
                    {muteStatus.until
                      ? `您已被禁言，至 ${new Date(muteStatus.until).toLocaleDateString('zh-CN')}`
                      : '您已被禁言'}
                  </div>
                ) : (
                  <>
                    <textarea
                      ref={inputRef}
                      style={inputStyle}
                      value={inputValue}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      placeholder="说点什么... 输入 @ 可以 @其他人"
                      rows={1}
                    />
                    <button
                      style={sendBtnStyle(!inputValue.trim() || publicChat.sending)}
                      onClick={handleSend}
                      disabled={!inputValue.trim() || publicChat.sending}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            /* 群组列表（「我的」：已加入的群 + 邀请 + 凭码加入入口） */
            <div style={groupsContainerStyle}>
              {/* 收到群邀请（「我的」tab 顶部，显眼入口） */}
              {activeTab === 'mine' && invitations.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#FF8C42' }}>
                    📬 收到群邀请（{invitations.length}）
                  </div>
                  {invitations.map(inv => {
                    const isHandling = handlingInviteGroupId === inv.groupId
                    return (
                      <div key={inv.groupId} style={groupCardStyle}>
                        <div style={groupCardHeaderStyle}>
                          <div style={groupAvatarStyle}>{inv.name[0]}</div>
                          <div style={groupInfoStyle}>
                            <div style={groupNameStyle}>{inv.name}</div>
                            <div style={groupDescStyle}>
                              {inv.description || `邀请你加入${inv.ownerName ? ` ${inv.ownerName} 的群` : '群组'}`}
                            </div>
                          </div>
                        </div>
                        <div style={groupMetaStyle}>
                          <span>👤 {inv.memberCount || 0} 人</span>
                          <span>👑 群主：{inv.ownerName || '未知'}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button
                            style={{ ...applyBtnStyle, flex: 1, background: '#88D8B0' }}
                            onClick={() => handleAcceptInvite(inv.groupId)}
                            disabled={isHandling}
                          >
                            {isHandling ? '...' : '同意加入'}
                          </button>
                          <button
                            style={{ ...applyBtnStyle, flex: 1, background: '#FF5252', color: '#fff' }}
                            onClick={() => handleRejectInvite(inv.groupId)}
                            disabled={isHandling}
                          >
                            拒绝
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              {groups.length === 0 && invitations.length === 0 ? (
                <div style={emptyStyle}>还没有加入群组，凭邀请码加入吧 👇</div>
              ) : (
                sortGroupsByUnread(groups, unread.byGroup).map(group => {
                  const isJoined = group.myRole === 'approved' || group.myRole === 'owner'
                  const isInvited = group.myRole === 'invited'
                  const isHandling = handlingInviteGroupId === group._id
                  const hasUnread = (unread.byGroup.get(group._id) || 0) > 0
                  return (
                    <div
                      key={group._id}
                      // 未读群整卡高亮（同名群/长列表里一眼锁定该进哪个）
                      style={hasUnread ? { ...groupCardStyle, background: '#FFF8F2', border: '1px solid #FF8C42' } : groupCardStyle}
                      onClick={() => {
                        if (isJoined) {
                          setActiveGroupId(group._id)
                        }
                      }}
                    >
                      <div style={groupCardHeaderStyle}>
                        {group.avatar ? (
                          <img style={groupAvatarStyle} src={group.avatar} alt="" />
                        ) : (
                          <div style={groupAvatarStyle}>{group.name[0]}</div>
                        )}
                        <div style={groupInfoStyle}>
                          <div style={groupNameStyle}>{group.name}</div>
                          <div style={groupDescStyle}>{group.description}</div>
                        </div>
                      </div>
                      <div style={groupMetaStyle}>
                        <span>👤 {group.memberCount || 0} 人</span>
                        <span>👑 群主：{group.ownerName || '未知'}</span>
                        {(unread.byGroup.get(group._id) || 0) > 0 && (
                          <span
                            style={{
                              marginLeft: 'auto',
                              padding: '1px 8px',
                              borderRadius: 9,
                              background: '#FF5252',
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {unread.byGroup.get(group._id)} 条新
                          </span>
                        )}
                      </div>
                      {/* 邀请你加入 — 优先显示 */}
                      {isInvited && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button
                            style={{
                              ...applyBtnStyle,
                              flex: 1,
                              background: '#88D8B0',
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleAcceptInvite(group._id)
                            }}
                            disabled={isHandling}
                          >
                            {isHandling ? '...' : '同意加入'}
                          </button>
                          <button
                            style={{
                              ...applyBtnStyle,
                              flex: 1,
                              background: '#FF5252',
                              color: '#fff',
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRejectInvite(group._id)
                            }}
                            disabled={isHandling}
                          >
                            拒绝
                          </button>
                        </div>
                      )}
                      {group.myRole === 'owner' && (
                        <div style={{ fontSize: 12, color: '#FF8C42', marginTop: 4 }}>
                          🏠 你是群主
                        </div>
                      )}
                    </div>
                  )
                })
              )}

              {/* 凭邀请码加入（隐私收紧后唯一自助加群入口）+ 群组管理 */}
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {!showJoinByCode ? (
                  <button
                    style={createGroupBtnStyle}
                    onClick={() => {
                      setShowJoinByCode(true)
                      setJoinPreview(null)
                      setJoinCodeError(null)
                    }}
                  >
                    🔑 凭邀请码加入群组
                  </button>
                ) : (
                  <div style={groupCardStyle}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#2C3E50', marginBottom: 8 }}>
                      凭邀请码加入
                    </div>
                    {joinPreview ? (
                      <>
                        <div style={{ fontSize: 13, color: '#546E7A', marginBottom: 10 }}>
                          即将加入：<span style={{ fontWeight: 600, color: '#2C3E50' }}>{joinPreview.groupName}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            style={{ ...applyBtnStyle, flex: 1, background: '#88D8B0' }}
                            onClick={handleJoinByCode}
                            disabled={joiningByCode}
                          >
                            {joiningByCode ? '加入中...' : '确认加入'}
                          </button>
                          <button
                            style={{ ...applyBtnStyle, flex: 1, background: '#E0E6ED', color: '#546E7A' }}
                            onClick={() => {
                              setJoinPreview(null)
                              setJoinCodeError(null)
                            }}
                          >
                            重新输入
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          style={searchInputStyle}
                          placeholder="输入 6 位邀请码"
                          value={joinCodeInput}
                          maxLength={6}
                          onChange={(e) => setJoinCodeInput(e.target.value.toUpperCase())}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleValidateCode()
                          }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            style={{ ...applyBtnStyle, flex: 1, background: '#88D8B0', marginTop: 0 }}
                            onClick={handleValidateCode}
                            disabled={!joinCodeInput.trim() || validatingCode}
                          >
                            {validatingCode ? '验证中...' : '验证'}
                          </button>
                          <button
                            style={{ ...applyBtnStyle, flex: 1, background: '#E0E6ED', color: '#546E7A', marginTop: 0 }}
                            onClick={() => {
                              setShowJoinByCode(false)
                              setJoinCodeInput('')
                              setJoinCodeError(null)
                            }}
                          >
                            收起
                          </button>
                        </div>
                      </>
                    )}
                    {joinCodeError && (
                      <div role="alert" style={{ fontSize: 12, color: '#E53935', marginTop: 8 }}>
                        {joinCodeError}
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => router.navigate('/group/manage')}
                  title="群组管理（创建 / 解散 / 设置 / 成员）"
                  style={{
                    ...createGroupBtnStyle,
                    background: '#fff',
                    color: '#FF8C42',
                    border: '1px solid #FF8C42',
                    marginBottom: 0,
                  }}
                >
                  ⚙️ 管理群组
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}
